import AddIcon from '@mui/icons-material/Add';
import { GridColDef, GridActionsCellItem, GridRowParams } from '@mui/x-data-grid-premium';
import { Box, Button, Container, Fab, Typography } from '@mui/material';
import React, { useRef } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { AsyncDialog, AsyncDialogRef } from '../../../components/AsyncDialog/AsyncDialog';
import { ConfirmationDialog, ConfirmationDialogData } from '../../../components/ConfirmationDialog';
import { fetchStatuses } from '../../../constants/fetchStatuses';
import {
  csvTemplatesSelector,
  deleteCsvTemplateLoadingSelector,
} from '../../../redux/modules/b2bThriftPlan/CsvTempletes/csvTempletes.selectors';
import {
  fetchCsvTemplates,
  deleteCsvTemplate,
} from '../../../redux/modules/b2bThriftPlan/CsvTempletes/csvTempletes.actions';
import { Table } from '../../../components/Table';
import { TableRef } from '../../../components/Table/Table';
import { locations } from '../../../routes/locations';
import { useAppDispatch } from '../../../redux/hooks';

export const CsvTempletes = () => {
  const dispatch = useAppDispatch();
  const tableRef = useRef<TableRef>(null);
  const asyncDialogRef = useRef<AsyncDialogRef<ConfirmationDialogData, boolean>>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { csvTemplates, fetchCsvTemplatesStatus, totalCsvTemplates } = useSelector(csvTemplatesSelector);
  const isDeleteLoading = useSelector(deleteCsvTemplateLoadingSelector);
  const isLoading = fetchCsvTemplatesStatus === fetchStatuses.pending;
  const navigate = useNavigate();

  const onAddNew = (): void => {
    navigate(locations.csvTemplate('create'));
  };

  const onEditCsvTemplate = (csvTemplateId: number): void => {
    navigate(locations.csvTemplate(csvTemplateId));
  };

  const onDeleteCsvTemplate = async (csvTemplateId: number, templateName: string): Promise<void> => {
    await asyncDialogRef.current?.show({
      title: 'Delete CSV Template',
      description: `Are you sure you want to delete "${templateName}"?`,
      onConfirm: async () => {
        try {
          const response = await dispatch(deleteCsvTemplate(csvTemplateId)).unwrap();
          const successMessage = response?.data?.message || 'Csv template deleted successfully!';
          enqueueSnackbar(successMessage, { variant: 'success' });
          tableRef.current?.reload();
        } catch (error: any) {
          const errorMessage = error?.message || 'Failed to delete template';
          enqueueSnackbar(errorMessage, { variant: 'error' });
          throw error;
        }
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      hideable: false,
      filterable: false,
      sortable: false,
      width: 100,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key={`edit-csv-template-${params.row.id}`}
          icon={<EditOutlinedIcon />}
          label="Edit"
          onClick={() => onEditCsvTemplate(params.row.id)}
        />,
        <GridActionsCellItem
          key={`delete-csv-template-${params.row.id}`}
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => onDeleteCsvTemplate(params.row.id, params.row.name)}
          disabled={isDeleteLoading}
        />,
      ],
    },
    {
      field: 'id',
      headerName: 'Id',
    },
    {
      field: 'name',
      headerName: 'Name',
      minWidth: 200,
    },
    {
      field: 'url',
      headerName: 'Url',
      sortable: false,
      filterable: false,
      minWidth: 140,
      renderCell: (params) => {
        const url = params.row?.url;

        if (!url) {
          return null;
        }

        return (
          <Button component="a" href={url} target="_blank" rel="noopener noreferrer" size="small">
            Download
          </Button>
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'date',
      minWidth: 100,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      type: 'date',
      minWidth: 100,
      renderCell: (params) => {
        if (!params.value) {
          return '';
        }
        return new Date(params.value).toLocaleDateString();
      },
    },
  ];

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 5 }}>
        <Typography variant="h4">
          Csv Templates &nbsp;
          <Fab color="primary" size="small" aria-label="Create Company Group" onClick={onAddNew} sx={{ mr: 1 }}>
            <AddIcon />
          </Fab>
        </Typography>
      </Box>
      <Table
        ref={tableRef}
        fetchItems={fetchCsvTemplates}
        rows={csvTemplates}
        columns={columns}
        loading={isLoading}
        rowCount={totalCsvTemplates}
      />
      <AsyncDialog ref={asyncDialogRef} dialog={ConfirmationDialog} />
    </Container>
  );
};
